import React, { useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'

const INITIAL_SHOW_COUNT = 4

function StarRating({ rating, size = 'sm' }) {
  const full = Math.floor(rating)
  const hasHalf = rating - full >= 0.5
  const sizeClass = size === 'sm' ? 'text-sm' : 'text-base'
  return (
    <span className={`${sizeClass} text-yellow-400`} aria-label={`${rating} stars`}>
      {'★'.repeat(full)}{hasHalf ? '½' : ''}{'☆'.repeat(5 - full - (hasHalf ? 1 : 0))}
    </span>
  )
}

/**
 * TestimonialSelector - Radio-table for selecting a real review from feed data.
 *
 * Props:
 * - reviews: Array of {reviewer_name, text, rating, is_verified}
 * - selectedIndex: Currently selected review index
 * - onSelect: (review, index) => void
 * - disabled: boolean
 */
function TestimonialSelector({ reviews = [], selectedIndex = 0, onSelect, disabled = false }) {
  const { t } = useTranslation('approval')
  const [showAll, setShowAll] = useState(false)

  const visibleReviews = showAll ? reviews : reviews.slice(0, INITIAL_SHOW_COUNT)
  const hasMore = reviews.length > INITIAL_SHOW_COUNT

  const handleSelect = useCallback((review, index) => {
    if (!disabled && onSelect) {
      onSelect(review, index)
    }
  }, [disabled, onSelect])

  if (!reviews || reviews.length === 0) {
    return (
      <div className="border border-dashed border-purple-300 bg-purple-50/50 rounded-lg p-6 text-center">
        <span className="text-2xl block mb-2">☆</span>
        <p className="text-sm text-gray-600">{t('socialProof.noReviews', 'Keine Kundenbewertungen verfügbar.')}</p>
        <p className="text-xs text-gray-500 mt-1">{t('socialProof.importHint', 'Importiere einen Google Product Reviews Feed.')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <h4 className="text-sm font-medium text-gray-700">
        {t('socialProof.selectTestimonial', 'Bewertung auswählen')}
      </h4>

      <div className="border border-gray-200 rounded-lg overflow-hidden divide-y divide-gray-100">
        {visibleReviews.map((review, index) => {
          const isSelected = index === selectedIndex
          return (
            <label
              key={index}
              className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors duration-150
                ${isSelected ? 'bg-purple-50 ring-1 ring-inset ring-purple-400' : 'hover:bg-purple-50/60'}
                ${disabled ? 'opacity-60 cursor-not-allowed' : ''}`}
            >
              <input
                type="radio"
                name="testimonial"
                checked={isSelected}
                onChange={() => handleSelect(review, index)}
                disabled={disabled}
                className="text-purple-600 focus:ring-purple-500"
              />
              <StarRating rating={review.rating} />
              <span className="text-sm font-medium text-gray-800 whitespace-nowrap">
                {review.reviewer_name}
              </span>
              {review.is_verified && (
                <span className="text-green-600 text-xs" title={t('socialProof.verified', 'Verifiziert')}>✓</span>
              )}
              <span className="text-sm text-gray-500 truncate flex-1">
                &ldquo;{review.text?.length > 60 ? review.text.slice(0, 57) + '...' : review.text}&rdquo;
              </span>
            </label>
          )
        })}
      </div>

      {hasMore && (
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{visibleReviews.length} {t('socialProof.of', 'von')} {reviews.length} {t('socialProof.reviews', 'Bewertungen')}</span>
          <button
            type="button"
            onClick={() => setShowAll(!showAll)}
            className="text-purple-600 hover:text-purple-800 font-medium"
          >
            {showAll
              ? t('socialProof.showLess', 'Weniger anzeigen')
              : t('socialProof.showAll', { count: reviews.length, defaultValue: `Alle ${reviews.length} anzeigen` })}
          </button>
        </div>
      )}
    </div>
  )
}

TestimonialSelector.propTypes = {
  reviews: PropTypes.arrayOf(PropTypes.shape({
    reviewer_name: PropTypes.string,
    text: PropTypes.string,
    rating: PropTypes.number,
    is_verified: PropTypes.bool,
  })),
  selectedIndex: PropTypes.number,
  onSelect: PropTypes.func,
  disabled: PropTypes.bool,
}

export default TestimonialSelector
